'use client'

import { clsx } from 'clsx'
import { ImageProps } from 'next/image'
import { LinkProps } from 'next/link'
import { BaseHubImage } from 'basehub/next-image'

import { ButtonLink } from '~/sotano-kit/primitives/button'
import { Heading } from '~/sotano-kit/primitives/heading'
import { BlogPost } from './fragments'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'

type BlogPostCardProps = LinkProps & { className?: string }

export const Root: React.FC<React.PropsWithChildren<BlogPostCardProps>> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <li>
      <article>
        <ButtonLink
          {...rest}
          unstyled
          className={clsx(
            className,
            'relative inline-block scroll-mt-[calc(var(--header-height)+theme(spacing.6))] rounded-lg border border-dark-border-solid bg-dark-bg transition-colors duration-150 ease-out hover:bg-dark-shade'
          )}
        >
          {children}
        </ButtonLink>
      </article>
    </li>
  )
}

export const Thumbnail: React.FC<ImageProps> = ({
  className,
  src,
  alt,
  ...rest
}) => {
  return (
    <picture className="relative aspect-[626/220] bg-dark-elevated rounded-t-[7px] overflow-hidden block">
      <BaseHubImage
        {...rest}
        className={clsx('object-cover', className)}
        src={src}
        alt={alt}
        quality={100}
        fill
        unoptimized
      />
    </picture>
  )
}

type BlogPostCardTitleProps = React.JSX.IntrinsicElements['h6']

export const Title: React.FC<BlogPostCardTitleProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <Heading
      {...rest}
      unstyled
      as="h6"
      className={clsx(className, 'font-medium')}
    >
      {children}
    </Heading>
  )
}

type BlogPostCardSubtitleProps = React.JSX.IntrinsicElements['p']

export const Subtitle: React.FC<BlogPostCardSubtitleProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <p
      {...rest}
      className={clsx(
        className,
        'text-dark-muted text-xs md:text-sm sm:leading-5'
      )}
    >
      {children}
    </p>
  )
}

type BlogPostCardFooterProps = React.JSX.IntrinsicElements['div']

export const Footer: React.FC<BlogPostCardFooterProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div
      {...rest}
      className={clsx(
        className,
        'flex flex-wrap justify-between md:grid md:grid-cols-[70%_1fr] gap-x-8 gap-y-4 mt-4'
      )}
    >
      {children}
    </div>
  )
}

type BlogPostCardAuthorsProps = React.JSX.IntrinsicElements['div'] & {
  authors: BlogPost['authorS']
  noLink?: boolean
}

export const Authors: React.FC<BlogPostCardAuthorsProps> = ({
  authors,
  className,
  noLink,
  ...rest
}) => {
  const avatars = authors.slice(0, 3).map((author) => ({
    ...author.avatar,
    alt: author.avatar?.alt ?? `${author.name}'s avatar`,
  }))

  const hasMultipleAuthors = authors.length > 2

  return (
    <div {...rest} className={className}>
      <p className="text-sm text-dark-muted leading-5">
        Author{hasMultipleAuthors && '(s)'}
      </p>
      <div className="mt-1.5 md:mt-2.5 text-sm text-dark-dim flex items-center">
        {avatars.map(({ url, alt }, index) => {
          const shouldRenderTooltip = hasMultipleAuthors
          const author = authors[index]

          return shouldRenderTooltip ? (
            <Tooltip key={index}>
              <TooltipTrigger className="cursor-default" asChild>
                {!noLink && author?.x ? (
                  <ButtonLink
                    href={author.x}
                    unstyled
                    focusStyled
                    target="_blank"
                    rel="noopener noreferer"
                    className="hover:underline relative border border-white/10 !rounded-full inline-block overflow-hidden w-5 h-5"
                    onClick={(e) => e.stopPropagation()}
                    style={{ left: -6 * index + 'px' }}
                  >
                    <BaseHubImage src={url} alt={alt} fill />
                  </ButtonLink>
                ) : (
                  <BaseHubImage
                    src={url}
                    alt={alt}
                    style={{ left: -6 * index + 'px' }}
                    className="inline-block align-middle border border-white/10 rounded-full relative"
                    width={20}
                    height={20}
                  />
                )}
              </TooltipTrigger>
              <TooltipContent>{author?._title}</TooltipContent>
            </Tooltip>
          ) : (
            <BaseHubImage
              key={index}
              src={url}
              alt={alt}
              style={{ left: -6 * index + 'px' }}
              className="inline-block align-middle border border-white/10 rounded-full relative"
              width={20}
              height={20}
            />
          )
        })}
        {hasMultipleAuthors ? (
          <span className="-ml-0.5">Multiple authors</span>
        ) : (
          authors.map((author, index) => (
            <span
              key={index}
              className={clsx(authors.length === 1 ? 'ml-2' : 'ml-1')}
            >
              {author.name}&nbsp;
              <span className="text-dark-faint">
                {!noLink && author.x ? (
                  <ButtonLink
                    href={author.x}
                    unstyled
                    focusStyled
                    target="_blank"
                    rel="noopener noreferer"
                    className="hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {author._title}
                  </ButtonLink>
                ) : (
                  <span>{author._title}</span>
                )}
                {index !== authors.length - 1 && ', '}
              </span>
            </span>
          ))
        )}
      </div>
    </div>
  )
}

type BlogPostCardDateProps = React.JSX.IntrinsicElements['div']

export const Date: React.FC<BlogPostCardDateProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div {...rest} className={className}>
      <p className="text-sm text-dark-muted leading-5">Published On</p>
      <p className="text-xs text-dark-dim leading-4 mt-1.5 md:mt-2.5 whitespace-nowrap">
        {children}
      </p>
    </div>
  )
}
